import { Service, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, filter, distinctUntilChanged } from 'rxjs';

import { AbstractionLayerConfig, environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LaunchdarklyService } from 'src/app/services/launchdarkly.service';

export type ImportStatus = 'PREPARING' | 'STARTED' | 'INDEXING' | 'COMPLETED' | 'FAILED';

export interface ActiveImport {
	mainSuiteId: string;
	mainSuiteName: string;
	environment: string;
	status: ImportStatus;
	documentsIndexed: number;
	totalDocuments: number;
	message: string;
}

interface ImportProgressEvent {
	environment: string;
	mainSuiteId: string | null;
	mainSuiteName: string | null;
	status: ImportStatus;
	documentsIndexed: number | null;
	totalDocuments: number | null;
	message: string | null;
	timestamp: string;
}

const COMPLETED_REMOVAL_DELAY_MS = 8000;
const KEEP_ALIVE_TIMEOUT_MS = 120_000;

@Service()
export class ImportProgressService implements OnDestroy {
	private authService = inject(AuthService);

	private environmentService = inject(EnvironmentService);

	private launchdarklyService = inject(LaunchdarklyService);

	private destroyRef = inject(DestroyRef);

	private readonly _activeImports$ = new BehaviorSubject<ActiveImport[]>([]);

	public readonly activeImports$ = this._activeImports$.asObservable();

	private ws: WebSocket | null = null;

	private keepAliveTimer: ReturnType<typeof setTimeout> | null = null;

	private readonly removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

	// Enable verbose logging in non-production environments
	private isDev = !environment.IsProduction;

	constructor() {
		this.startListening();
	}

	private startListening(): void {
		combineLatest({
			userData: this.authService.userData$,
			environment: this.environmentService.environment$,
			flags: this.launchdarklyService.launchDarklyFlags$,
		})
			.pipe(
				filter(({ userData, environment }) => !!userData.isLoggedIn && !!environment),
				distinctUntilChanged((a, b) => a.userData.sessionID === b.userData.sessionID && a.environment === b.environment && a.flags['staging'] === b.flags['staging']),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(({ userData, environment, flags }) => {
				const useStaging = flags['staging'] !== false;
				this.reconnect(userData.sessionID ?? 'anonymous', environment!, useStaging);
			});
	}

	private reconnect(sessionToken: string, environment: string, useStaging: boolean): void {
		// Clear imports and timers when switching environment — old imports won't receive
		// events for the new environment and would otherwise get stuck
		this.clearActiveImports();
		this.disconnect();

		const httpUri = useStaging ? AbstractionLayerConfig.stagingUri : AbstractionLayerConfig.releaseUri;
		const realtimeUri = useStaging ? AbstractionLayerConfig.realtimeStagingUri : AbstractionLayerConfig.realtimeReleaseUri;
		const httpHost = new URL(httpUri).hostname;

		// AppSync requires auth headers base64-encoded in URL query params.
		const header = encodeURIComponent(btoa(JSON.stringify({ authorization: sessionToken, host: httpHost })));
		const payload = encodeURIComponent(btoa('{}'));
		const wsUrl = `${realtimeUri}?header=${header}&payload=${payload}`;

		if (this.isDev) {
			console.log('[ImportProgressService] Connecting', { realtimeUri, httpHost, environment });
		}

		// Use 'graphql-ws' subprotocol — the protocol AppSync Realtime natively speaks.
		// NOT 'graphql-transport-ws' (used by the graphql-ws npm package) which AppSync rejects.
		this.ws = new WebSocket(wsUrl, 'graphql-ws');

		this.ws.onopen = () => {
			if (this.isDev) {
				console.log('[ImportProgressService] WebSocket open — sending connection_init');
			}
			this.ws!.send(JSON.stringify({ type: 'connection_init' }));
		};

		this.ws.onmessage = event => {
			let msg: any;
			try {
				msg = JSON.parse(event.data);
			} catch {
				return;
			}

			switch (msg.type) {
				case 'connection_ack':
					if (this.isDev) {
						console.log('[ImportProgressService] connection_ack — subscribing for', environment);
					}
					this.sendSubscribe(sessionToken, httpHost, environment);
					break;

				case 'ka':
					this.resetKeepAlive();
					break;

				case 'start_ack':
					if (this.isDev) {
						console.log('[ImportProgressService] Subscription acknowledged');
					}
					break;

				case 'data': {
					const progressEvent: ImportProgressEvent | undefined = msg?.payload?.data?.analyzerOnImportProgress;
					if (progressEvent) {
						if (this.isDev) {
							console.log('[ImportProgressService] Event received:', progressEvent);
						}
						this.handleEvent(progressEvent);
					}
					break;
				}

				case 'connection_error':
					console.error('[ImportProgressService] connection_error from AppSync:', msg.payload);
					break;

				case 'error':
					console.error('[ImportProgressService] Subscription error from AppSync:', msg.payload);
					break;

				case 'complete':
					if (this.isDev) {
						console.log('[ImportProgressService] Subscription complete');
					}
					break;
			}
		};

		this.ws.onerror = event => {
			console.error('[ImportProgressService] WebSocket error', event);
		};

		this.ws.onclose = event => {
			console.warn('[ImportProgressService] WebSocket closed', { code: event.code, reason: event.reason, clean: event.wasClean });
			this.clearKeepAlive();
		};
	}

	private sendSubscribe(sessionToken: string, httpHost: string, environment: string): void {
		if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

		const query = `subscription AnalyzerOnImportProgress($environment: String!) {
      analyzerOnImportProgress(environment: $environment) {
        environment mainSuiteId mainSuiteName status documentsIndexed totalDocuments message timestamp
      }
    }`;

		// AppSync start message: query goes inside "data" as a JSON string, auth in extensions.
		const message = {
			id: 'import-progress-sub',
			type: 'start',
			payload: {
				data: JSON.stringify({ query, variables: { environment } }),
				extensions: {
					authorization: {
						authorization: sessionToken,
						host: httpHost,
					},
				},
			},
		};

		this.ws.send(JSON.stringify(message));
	}

	private resetKeepAlive(): void {
		this.clearKeepAlive();
		this.keepAliveTimer = setTimeout(() => {
			console.warn('[ImportProgressService] Keep-alive timeout — reconnecting');
			if (this.ws) {
				this.disconnect();
				// Trigger reconnect via source observable on next emission; force by closing
			}
		}, KEEP_ALIVE_TIMEOUT_MS);
	}

	private clearKeepAlive(): void {
		if (this.keepAliveTimer !== null) {
			clearTimeout(this.keepAliveTimer);
			this.keepAliveTimer = null;
		}
	}

	private clearActiveImports(): void {
		this._activeImports$.next([]);
		this.removalTimers.forEach(t => clearTimeout(t));
		this.removalTimers.clear();
	}

	private disconnect(): void {
		this.clearKeepAlive();
		if (this.ws) {
			this.ws.onopen = null;
			this.ws.onmessage = null;
			this.ws.onerror = null;
			this.ws.onclose = null;
			if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
				this.ws.close();
			}
			this.ws = null;
		}
	}

	private handleEvent(event: ImportProgressEvent): void {
		const id = event.mainSuiteId ?? event.environment;
		let current = this._activeImports$.getValue();

		// Replace any PREPARING placeholder for this environment with the real event
		if (event.mainSuiteId) {
			current = current.filter(i => !(i.status === 'PREPARING' && i.environment === event.environment));
		}

		const updated: ActiveImport = {
			mainSuiteId: id,
			mainSuiteName: event.mainSuiteName ?? event.mainSuiteId ?? '',
			environment: event.environment,
			status: event.status,
			documentsIndexed: event.documentsIndexed ?? 0,
			totalDocuments: event.totalDocuments ?? 0,
			message: event.message ?? '',
		};

		const exists = current.find(i => i.mainSuiteId === id);
		const next = exists ? current.map(i => (i.mainSuiteId === id ? updated : i)) : [...current, updated];

		this._activeImports$.next(next);

		if (event.status === 'COMPLETED' || event.status === 'FAILED') {
			const existing = this.removalTimers.get(id);
			if (existing) clearTimeout(existing);
			const timer = setTimeout(() => {
				const latest = this._activeImports$.getValue().filter(i => i.mainSuiteId !== id);
				this._activeImports$.next(latest);
				this.removalTimers.delete(id);
			}, COMPLETED_REMOVAL_DELAY_MS);
			this.removalTimers.set(id, timer);
		}
	}

	/**
	 * Notifies that an import file has been uploaded and we're waiting for the first backend event.
	 * Shows the progress banner immediately with "Preparing..." status.
	 * @param environment
	 * @param fileName
	 */
	public notifyPreparing(environment: string, fileName?: string): void {
		const id = `preparing-${environment}-${Date.now()}`;
		const placeholder: ActiveImport = {
			mainSuiteId: id,
			mainSuiteName: fileName ?? 'Import file',
			environment,
			status: 'PREPARING',
			documentsIndexed: 0,
			totalDocuments: 0,
			message: '',
		};
		const current = this._activeImports$.getValue();
		this._activeImports$.next([...current, placeholder]);
	}

	ngOnDestroy(): void {
		this.disconnect();
		this.removalTimers.forEach(t => clearTimeout(t));
	}
}
